import axios from "axios";
import { toast } from "react-toastify";
import { generateSesid } from "utilities/generateSesid";
import { buildXborderFields, buildUsdPayloadFromCrossBorder, validateXborder } from "./helpers";

const buildSource = (provider) => {
    const name = provider?.name || provider?.bankName || "";
    return name || undefined;
};

const buildSavedRecipientReference = (...sources) => {
    for (const source of sources) {
        if (source?.beneficiaryId) return { beneficiaryId: source.beneficiaryId };
        if (source?.transactionId) return { transactionId: source.transactionId };
    }

    return {};
};

const normalizeUsdPaymentRail = (usd, accountType) => {
    const rawRail = String(
        usd?.paymentRail || usd?.payment_rail || usd?.paymentMethod || usd?.paymentTrail || ""
    ).toLowerCase();
    if (rawRail === "iban") return "wire";
    if (["ach", "rtp", "wire", "swift"].includes(rawRail)) return rawRail;
    if (accountType === "iban") return "wire";
    if (accountType === "international_wire" || accountType === "swift") return "swift";
    return "ach";
};

const normalizeUsdLocation = (usd, accountType) => {
    const rawLocation = String(
        usd?.location || usd?.recipient_location || usd?.recipientLocation || ""
    ).toLowerCase();
    if (rawLocation === "us") return "US";
    if (["nonus", "non_us", "non-us", "outside_us", "outside-us"].includes(rawLocation)) {
        return "nonUS";
    }
    return accountType === "us" ? "US" : "nonUS";
};

const buildOtcRecipient = ({ currencyTo, fields, bankName, usd }) => {
    const to = String(currencyTo || "").toUpperCase();

    if (to === "USD") {
        const rawAccountType = String(usd?.account_type || "us").toLowerCase();
        const accountType = rawAccountType === "swift" ? "international_wire" : rawAccountType;
        const accountOwnerType = (usd?.account_owner_type || "individual").trim();

        const firstName = String(usd?.first_name || "").trim();
        const lastName = String(usd?.last_name || "").trim();
        const businessName = String(usd?.business_name || "").trim();

        const accountOwnerName = (
            usd?.account_owner_name ||
            (accountOwnerType === "corporate"
                ? businessName
                : [firstName, lastName].filter(Boolean).join(" ").trim()) ||
            usd?.accountName ||
            usd?.recipientName ||
            ""
        ).trim();

        const paymentRail = normalizeUsdPaymentRail(usd, accountType);
        const location = normalizeUsdLocation(usd, accountType);

        const address = usd?.address || {};
        const addressBlock = {
            street_line_1: String(address.street_line_1 || "").trim(),
            street_line_2: String(address.street_line_2 || "").trim(),
            city: String(address.city || "").trim(),
            state: String(address.state || "").trim(),
            subdivision: String(address.subdivision || "").trim(),
            postal_code: String(address.postal_code || "").trim(),
            country: String(address.country || "").trim(),
        };

        const bankAddress = usd?.bank_address || {};
        const bankAddressBlock = {
            street_line_1: String(bankAddress.street_line_1 || "").trim(),
            street_line_2: String(bankAddress.street_line_2 || "").trim(),
            city: String(bankAddress.city || "").trim(),
            state: String(bankAddress.state || "").trim(),
            postal_code: String(bankAddress.postal_code || "").trim(),
            country: String(bankAddress.country || "").trim(),
        };

        let methodBlock = {};
        if (accountType === "iban") {
            methodBlock = {
                iban: {
                    account_number: String(usd?.iban?.account_number || usd?.accountNumber || "").replace(/\s+/g, ""),
                    bic: String(usd?.iban?.bic || usd?.swiftBic || "").trim(),
                    country: String(usd?.iban?.country || addressBlock.country || "").trim(),
                },
            };
        } else if (accountType === "international_wire") {
            methodBlock = {
                swift: {
                    account_number: String(usd?.swift?.account_number || usd?.accountNumber || "").trim(),
                    bic: String(usd?.swift?.bic || usd?.swiftBic || "").trim(),
                    country: String(usd?.swift?.country || addressBlock.country || "").trim(),
                },
            };
        } else {
            methodBlock = {
                account: {
                    account_number: String(usd?.account?.account_number || usd?.accountNumber || "").trim(),
                    routing_number: String(
                        usd?.account?.routing_number || usd?.routingNumber || ""
                    ).replace(/[^\d]/g, ""),
                    checking_or_savings: String(usd?.account?.checking_or_savings || "checking").trim(),
                },
            };
        }

        return {
            account_owner_name: accountOwnerName,
            account_owner_type: accountOwnerType,
            first_name: accountOwnerType === "individual" ? firstName : "",
            last_name: accountOwnerType === "individual" ? lastName : "",
            business_name: accountOwnerType === "corporate" ? businessName : "",
            email: String(usd?.email || "").trim(),
            relationship: String(usd?.relationship || "").trim(),
            bank_name: String(usd?.bank_name || bankName || usd?.bankName || "").trim(),
            account_type: accountType,
            paymentRail,
            location,
            address: addressBlock,
            ...(accountType === "us" ? { bank_address: bankAddressBlock } : {}),
            ...methodBlock,
        };
    }

    const destinationFields = fields || {};
    const accountNumber = destinationFields.accountNumber || destinationFields.iban || "";
    const bankCode =
        destinationFields.bankCode ||
        destinationFields.routingNumber ||
        destinationFields.swiftBic ||
        destinationFields.swift ||
        destinationFields.bic ||
        destinationFields.sortCode ||
        destinationFields.transitNumber ||
        destinationFields.institutionNumber ||
        "";

    return {
        accountName: String(
            destinationFields.accountName ||
                destinationFields.recipientName ||
                destinationFields.account_owner_name ||
                ""
        ).trim(),
        bankName: String(bankName || destinationFields.bankName || destinationFields.bank_name || "").trim(),
        accountNumber: String(accountNumber).trim(),
        bankCode: String(bankCode).trim(),
    };
};

export async function submitReviewTransfer({
    pin,
    businessId,
    isCryptoFlow,
    isCryptoToFiatFlow,
    isCrossBorder,
    isBankRecipient,
    currency,
    cryptoTransfer,
    cfAmount,
    cfCoin,
    cfNetwork,
    cfRecipient,
    cfMemo,
    cfPayoutCurrency,
    cb,
    cbTo,
    cbFrom,
    cbFields,
    cbBankName,
    cbMemo,
    cbInvoice,
    cbOtherDoc,
    cbUsd,
    cbAmount,
    cbSource,
    usd,
    usdPayloadFromStore,
    recipientMemo,
    recipientAmount,
    recipientInvoice,
    recipientOtherDocument,
    transferData,
    bankRecipient,
    bankProvider,
}) {
    const headers = { headers: { "x-business-id": businessId } };
    const sesid = generateSesid(businessId);
    const savedRecipientReference = buildSavedRecipientReference(
        bankRecipient,
        usdPayloadFromStore,
        transferData?.recipient
    );

    let endpoint = "";
    let method = "post";
    let payload = {};

    if (isCryptoFlow) {
        endpoint = "/v1/wallets/transfer-crypto";
        payload = {
            address: cryptoTransfer?.address || "",
            amount: String(cryptoTransfer?.amount ?? ""),
            naration: cryptoTransfer?.naration || "",
            currency: cryptoTransfer?.token || "",
            network: cryptoTransfer?.network,
            pin,
            ...(cryptoTransfer?.invoice && {
                invoice: {
                    url: cryptoTransfer.invoice.url || "",
                    name: cryptoTransfer.invoice.name || "",
                },
            }),
            ...(cryptoTransfer?.supportDocument && {
                supportDocument: {
                    url: cryptoTransfer.supportDocument.url || "",
                    name: cryptoTransfer.supportDocument.name || "",
                },
            }),
        };
    } else if (isCryptoToFiatFlow) {
        endpoint = "/v1/wallets/crypto/bank-withdraw";
        payload = {
            amount: String(cfAmount || ""),
            recipient: {
                institution: cfRecipient?.institution || "",
                accountIdentifier: cfRecipient?.accountIdentifier || "",
                accountName: cfRecipient?.accountName || "",
                bankName: cfRecipient?.bankName || "",
                memo: cfMemo || "",
                currency: cfPayoutCurrency || "",
            },
            pin,
            currency: cfCoin,
            network: cfNetwork || "",
        };
    } else if (isCrossBorder) {
        const crossBorderSavedReference = buildSavedRecipientReference(cb?.recipient);
        const isOtcRequest = Boolean(
            cb?.usePreferredRate && Number(cb?.preferredRate) > 0
        );
        const isRecipientSkipped = Boolean(isOtcRequest && cb?.recipientSkipped);

        if (isOtcRequest) {
            const recipient = buildOtcRecipient({
                currencyTo: cbTo,
                fields: cbFields,
                bankName: cbBankName,
                usd: cbUsd,
            });
            const isUsdOtcRequest = String(cbTo).toUpperCase() === "USD";
            const requestRecipient = isRecipientSkipped
                ? isUsdOtcRequest
                    ? {
                          location: recipient.location,
                          paymentRail: recipient.paymentRail,
                      }
                    : undefined
                : recipient;

            if (!isRecipientSkipped && isUsdOtcRequest) {
                const missingUsdRecipientFields = [];
                if (!recipient.account_owner_name) {
                    missingUsdRecipientFields.push("recipient.account_owner_name");
                }
                if (!recipient.bank_name) missingUsdRecipientFields.push("recipient.bank_name");
                if (!recipient.location) missingUsdRecipientFields.push("recipient.location");
                if (!recipient.paymentRail) {
                    missingUsdRecipientFields.push("recipient.paymentRail");
                }
                if (recipient.account_type === "us") {
                    if (!recipient.account?.account_number) {
                        missingUsdRecipientFields.push("recipient.account.account_number");
                    }
                    if (!recipient.account?.routing_number) {
                        missingUsdRecipientFields.push("recipient.account.routing_number");
                    }
                }
                if (recipient.account_type === "iban") {
                    if (!recipient.iban?.account_number) {
                        missingUsdRecipientFields.push("recipient.iban.account_number");
                    }
                    if (!recipient.iban?.bic) {
                        missingUsdRecipientFields.push("recipient.iban.bic");
                    }
                }
                if (recipient.account_type === "international_wire") {
                    if (!recipient.swift?.account_number) {
                        missingUsdRecipientFields.push("recipient.swift.account_number");
                    }
                    if (!recipient.swift?.bic) {
                        missingUsdRecipientFields.push("recipient.swift.bic");
                    }
                }

                if (missingUsdRecipientFields.length) {
                    toast.error(
                        `Missing required fields for USD: ${missingUsdRecipientFields.join(", ")}`
                    );
                    return undefined;
                }
            }

            if (cb?.isResumingOtcOffer && cb?.otcRequestId) {
                method = "patch";
                endpoint = `/v1/otc/${cb.otcRequestId}`;
                payload = {
                    pin,
                    ...(crossBorderSavedReference?.beneficiaryId
                        ? { beneficiaryId: crossBorderSavedReference.beneficiaryId }
                        : {}),
                    ...(cb?.rateKey ? { rateKey: cb.rateKey } : {}),
                    recipient,
                    narration: String(cbMemo || "").trim(),
                    ...(cbOtherDoc?.url
                        ? { supportDocument: { url: cbOtherDoc.url } }
                        : {}),
                    ...(cbInvoice?.url ? { invoice: { url: cbInvoice.url } } : {}),
                };
            } else {
                const customerRate = Number(cb?.preferredRate) || 0;
                endpoint = "/v1/otc";
                payload = {
                    pin,
                    customerRate,
                    note: String(cb?.otcNotes || "").trim(),
                    amount: Number(cbAmount) || 0,
                    currency: String(cbFrom || "").toUpperCase(),
                    currencyTo: String(cbTo || "").toUpperCase(),
                    ...crossBorderSavedReference,
                    ...(cb?.rateKey ? { rateKey: cb.rateKey } : {}),
                    ...(requestRecipient ? { recipient: requestRecipient } : {}),
                    narration: String(cbMemo || "").trim(),
                    ...(cbOtherDoc?.url
                        ? { supportDocument: { url: cbOtherDoc.url } }
                        : {}),
                    ...(cbInvoice?.url ? { invoice: { url: cbInvoice.url } } : {}),
                    ...(cbSource && { source: cbSource }),
                };
            }
        } else if (String(cbTo).toUpperCase() === "USD") {
            endpoint = "/v1/wallets/cross-currency/transfer";
            const usdPayload = buildUsdPayloadFromCrossBorder(cb, pin, sesid);
            const acctType = String(cb?.recipient?.usd?.account_type || "us").toLowerCase();
            const missing = [];

            if (!usdPayload.bank_name) missing.push("bank_name");
            if (!usdPayload.account_owner_name) missing.push("account_owner_name");
            if (!usdPayload.address?.street_line_1) missing.push("address.street_line_1");
            if (!usdPayload.address?.city) missing.push("address.city");
            if (!usdPayload.address?.state) missing.push("address.state");
            if (!usdPayload.address?.postal_code) missing.push("address.postal_code");
            if (!usdPayload.address?.country) missing.push("address.country");

            if (acctType === "us") {
                if (!usdPayload.account?.routing_number) missing.push("account.routing_number");
                if (!usdPayload.account?.account_number) missing.push("account.account_number");
            }
            if (acctType === "iban") {
                if (!usdPayload.iban?.account_number) missing.push("iban.account_number");
                if (!usdPayload.iban?.bic) missing.push("iban.bic");
                if (!usdPayload.iban?.country) missing.push("iban.country");
            }
            if (acctType === "international_wire" || acctType === "swift") {
                if (!usdPayload.swift?.account_number) missing.push("swift.account_number");
                if (!usdPayload.swift?.bic) missing.push("swift.bic");
                if (!usdPayload.swift?.country) missing.push("swift.country");
            }

            if (
                !isRecipientSkipped &&
                !crossBorderSavedReference.beneficiaryId &&
                !crossBorderSavedReference.transactionId &&
                missing.length
            ) {
                toast.error(`Missing required fields for USD: ${missing.join(", ")}`);
                return undefined;
            }

            payload = {
                ...usdPayload,
                ...crossBorderSavedReference,
                ...(cbInvoice?.url && { invoice: cbInvoice.url }),
                ...(cbOtherDoc?.url && { supportDocument: cbOtherDoc.url }),
                ...(cbSource && { source: cbSource }),
            };
        } else {
            endpoint = "/v1/wallets/cross-currency/transfer";
            const fields = buildXborderFields(cbTo, cbFields, cbBankName, cbUsd);
            const { ok, missing } = validateXborder(cbTo, fields, cbUsd?.account_type || "us");
            if (
                !isRecipientSkipped &&
                !crossBorderSavedReference.beneficiaryId &&
                !crossBorderSavedReference.transactionId &&
                !ok
            ) {
                toast.error(`Missing required fields for ${cbTo}: ${missing.join(", ")}`);
                return undefined;
            }

            payload = {
                amount: String(cbAmount || ""),
                sesid,
                pin,
                naration: cbMemo || "",
                currencyFrom: (cbFrom || "").toUpperCase(),
                isConversion: false,
                currencyTo: (cbTo || "").toUpperCase(),
                ...(isRecipientSkipped ||
                crossBorderSavedReference.beneficiaryId ||
                crossBorderSavedReference.transactionId
                    ? {}
                    : fields),
                ...crossBorderSavedReference,
                ...(cb?.rateKey ? { rateKey: cb.rateKey } : {}),
                ...(cbInvoice?.url && { invoice: cbInvoice.url }),
                ...(cbOtherDoc?.url && { supportDocument: cbOtherDoc.url }),
                ...(cbSource && { source: cbSource }),
            };
        }
    } else if (isBankRecipient && String(currency).toUpperCase() === "USD") {
        endpoint = "/v1/wallets/fx/transfer";
        payload = {
            ...usdPayloadFromStore,
            ...savedRecipientReference,
            pin,
            sesid,
            naration: (usd?.narration || recipientMemo || transferData?.memo || "").trim(),
            amount: String(usd?.amount || recipientAmount || transferData?.amount || ""),
            currency: "USD",
            currencyFrom: "USD",
        };

        if (recipientInvoice?.url) payload.invoice = recipientInvoice.url;
        if (recipientOtherDocument?.url) payload.supportDocument = recipientOtherDocument.url;

        const usdSource = buildSource(bankProvider);
        if (usdSource) payload.source = usdSource;
    } else if (isBankRecipient) {
        endpoint = "/v1/wallets/transfer";
        const source = buildSource(bankProvider);
        payload = {
            amount: recipientAmount || "N/A",
            naration: (recipientMemo || "").trim() || "N/A",
            invoice: recipientInvoice?.url || "N/A",
            supportDocument: recipientOtherDocument?.url || "N/A",
            currency,
            sesid,
            pin,
            ...savedRecipientReference,
        };

        if (source) payload.source = source;

        if (!savedRecipientReference.beneficiaryId && !savedRecipientReference.transactionId) {
            payload = {
                ...payload,
                accountname: bankRecipient?.name || "N/A",
                accountnumber: bankRecipient?.accountNumber || "N/A",
                accountcode: bankRecipient?.bankCode || "N/A",
                bankname: bankRecipient?.bankName || "N/A",
            };
        }
    } else {
        endpoint = "/v1/wallets/transfer_to_wallet";
        const source = buildSource(transferData?.provider);
        payload = {
            amount: transferData?.amount || "N/A",
            naration: (transferData?.memo || "").trim() || "N/A",
            invoice: transferData?.invoice?.url || "N/A",
            supportDocument: transferData?.otherDocument?.url || "N/A",
            accountnumber: transferData?.recipient?.accountNumber || "N/A",
            currency: transferData?.currency || "N/A",
            pin,
            ...(source && { source }),
        };
    }

    const { data } = await axios[method](endpoint, payload, headers);
    const result = data?.data ?? data ?? {};
    return (
        result?.transactionId ||
        result?.transaction?._id ||
        result?.transaction?.id ||
        result?._id ||
        result?.id ||
        result?.otc?._id ||
        result?.otc?.id ||
        result?.request?._id ||
        result?.request?.id ||
        data?.transactionId ||
        null
    );
}
